import { type FC, useEffect } from "react";

import { startCase } from "lodash";
import { Controller, type UseFormReturn } from "react-hook-form";

import { Input, Select } from "@alpacahq/ui/old";

import { Field } from "~/v2/components/form";
import { WITHDRAW_REASONS } from "../common/const";
import {
  getIsReasonDetailsRequired,
  getIsReasonRequired,
} from "../common/util";
import { getCorrectAmount, type TransferSchema } from "./schema";

type ReasonFieldsProps = {
  form: UseFormReturn<TransferSchema>;
  withdrawableAmount: number;
};

export const ReasonFields: FC<ReasonFieldsProps> = ({
  form,
  withdrawableAmount,
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const amount = getCorrectAmount(watch("amount"));
  const isWithdrawalInvalid = amount > 0 && amount > withdrawableAmount;

  const isReasonRequired =
    !isWithdrawalInvalid && getIsReasonRequired(amount, withdrawableAmount);
  const isReasonDetailsRequired =
    isReasonRequired && getIsReasonDetailsRequired(watch("reason"));

  // Clear reason fields when necessary conditions are not met
  useEffect(() => {
    if (!getIsReasonRequired(amount, withdrawableAmount ?? 0)) {
      setValue("reason", undefined, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("reason_details", undefined, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [amount, setValue, withdrawableAmount]);

  return (
    <>
      {isReasonRequired && (
        <div className="py-4">
          <Field
            label={"Reason (Optional)"}
            required={false}
            error={!!errors.reason}
            errorMsg={errors?.reason?.message}
          >
            <Controller
              name={"reason"}
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Please select the reason for withdrawal"
                  options={WITHDRAW_REASONS.map((reason) => ({
                    label: startCase(reason.toLowerCase()),
                    value: reason,
                  }))}
                />
              )}
            />
          </Field>
        </div>
      )}
      {isReasonDetailsRequired && (
        <div className="py-4">
          <Field
            label={"Reason Details"}
            required={false}
            error={!!errors.reason_details}
            errorMsg={errors?.reason_details?.message}
          >
            <Controller
              name={"reason_details"}
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Input {...field} placeholder="Please provide more details" />
              )}
            />
          </Field>
        </div>
      )}
    </>
  );
};
